import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import { errorToast } from "@/v2-ui/toast"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import Badge from "@/v2-backoffice-client/components/badge"
import BadgeList from "@/v2-backoffice-client/components/badgeList"
import LicenseAddOnTableRowToolbar from "@/v2-backoffice-client/license/addOns/LicenseAddOnTableRowToolbar"
import LicenseAddOnManageButton from "@/v2-backoffice-client/license/addOns/manage/LicenseAddOnManageButton"
import {
  ADD_ON_STANDING_VARIANT,
  addOnStandingOf,
  describeAddOnLicenseNeeded,
  describeAddOnStanding,
} from "@/v2-backoffice-client/license/addOns/license.addOns.utils"
import { splitPackageNames } from "@/v2-backoffice-client/license/changes/license.changes.utils"
import { useFeatureGroupsQuery } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.query"
import { useLicenseStatusesQuery } from "@/v2-backoffice-client/license/license.statuses.query"
import type { LicenseAddOnsQuery } from "@/v2-backoffice-client/license/addOns/__types__/license.addOns.query"

type CustomerAddOn = LicenseAddOnsQuery["getCustomerAddOns"][number]

function createAddOnColumns(input: {
  who: string
  statusNameOf(code: number): string | undefined
  packageIdByName: ReadonlyMap<string, number>
}) {
  const { who, statusNameOf, packageIdByName } = input

  return createColumns<CustomerAddOn>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      AddOnName: {
        id: "AddOnName",
        label: "Add-on",
        defaultSize: 170,
        // Push: the row lives inside the customer panel, so the add-on's
        // panel stacks on top of it (breadcrumb back).
        to: ({ row }) => `>>?v=addOn:${row.original.AddOnId}`,
        renderCell({ row }) {
          const { AddOnName, IsCustom } = row.original
          if (!IsCustom) return AddOnName
          return (
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate">{AddOnName}</span>
              <Badge
                size="tiny"
                variant="neutral"
                tooltip="Bespoke: composed for this customer only"
              >
                Bespoke
              </Badge>
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.AddOnName
        },
      },
      Standing: {
        id: "Standing",
        label: "Status",
        description:
          "Works: a live license the add-on goes with carries it, for the people on that license. Needs a license: the customer holds no license it goes with, so it gives nothing yet. License not live: the license it goes with is not live right now.",
        defaultSize: 130,
        enableGrouping: true,
        getValue: (row) => addOnStandingOf(row),
        renderCell({ row }) {
          const standing = addOnStandingOf(row.original)
          return (
            <Badge
              size="sm"
              variant={ADD_ON_STANDING_VARIANT[standing]}
              tooltip={describeAddOnStanding(row.original, who, statusNameOf)}
            >
              {standing}
            </Badge>
          )
        },
      },
      Carriers: {
        id: "Carriers",
        label: "Goes with",
        description:
          "The license the add-on rides at this customer: it works while that license is live, for the people on it, and stops with it. When the customer holds no such license, the license it would need.",
        defaultSize: 190,
        getValue: (row) =>
          row.Carriers.map(
            (carrier) => carrier.PlanName ?? carrier.ProductName,
          ).join(", ") || describeAddOnLicenseNeeded(row.ProductNames),
        renderCell({ row }) {
          const { Carriers, ProductNames } = row.original
          return (
            <BadgeList
              items={Carriers.map((carrier) => ({
                key: String(carrier.LicenseId),
                label: carrier.PlanName ?? carrier.ProductName,
                to:
                  carrier.PlanId != null
                    ? `>>?v=plan:${carrier.PlanId}`
                    : undefined,
                variant: carrier.IsLive ? "neutral" : "outline",
                hint: statusNameOf(carrier.LicenseStatusCode),
                tooltip: `${carrier.ProductName} license, ${statusNameOf(carrier.LicenseStatusCode)?.toLowerCase() ?? "status unknown"}`,
              }))}
              overflowTitle="Goes with"
              emptyLabel={
                <span className="text-muted-foreground">
                  {describeAddOnLicenseNeeded(ProductNames)}
                </span>
              }
            />
          )
        },
      },
      FeatureGroupNames: {
        id: "FeatureGroupNames",
        label: "Unlocks",
        description:
          "The packages the add-on gives on top of the license, each opening its panel.",
        defaultSize: 200,
        getValue: (row) => row.FeatureGroupNames ?? "",
        renderCell({ row }) {
          return (
            <BadgeList
              items={splitPackageNames(row.original.FeatureGroupNames).map(
                (name) => {
                  const packageId = packageIdByName.get(name)
                  return {
                    label: name,
                    to:
                      packageId === undefined
                        ? undefined
                        : `>>?v=featureGroup:${packageId}`,
                  }
                },
              )}
              overflowTitle="Unlocks"
              emptyLabel={<span className="text-destructive">Nothing yet</span>}
            />
          )
        },
        exportValue({ row }) {
          return row.original.FeatureGroupNames ?? ""
        },
      },
      FeatureCount: {
        id: "FeatureCount",
        label: "Features",
        description: "How many features the add-on's packages unlock.",
        defaultSize: 80,
        defaultHidden: true,
        renderCell({ row }) {
          return row.original.FeatureCount || null
        },
      },
      GrantedAt: {
        id: "GrantedAt",
        label: "Added",
        defaultSize: 90,
        renderCell({ row }) {
          const value = row.original.GrantedAt
          return value ? formatDateString(value) : null
        },
      },
      GrantedByUserName: {
        id: "GrantedByUserName",
        label: "Added by",
        defaultSize: 130,
        defaultHidden: true,
      },
    },
  })
}

export type LicenseAddOnTableProps = Omit<
  TableProps<CustomerAddOn>,
  "data" | "columns"
> & {
  addOns: CustomerAddOn[]
  isLoading?: boolean
  error?: Error
  customerId: number
  customerName?: string
  onRefresh?(): void
}

// One customer's add-ons inside the panel's Licenses & add-ons tab, the
// same shell as the Licenses table above it: one row per add-on, whether it
// works for this customer right now, the license it rides, what it unlocks,
// when it was added, and the primary action (Add add-on) in the toolbar.
// An add-on with no live license to go with says so in its Status, with
// the why on hover, instead of looking like it works.
function LicenseAddOnTable(props: LicenseAddOnTableProps) {
  const {
    className,
    addOns,
    isLoading,
    error,
    customerId,
    customerName,
    onRefresh,
    ...tableProps
  } = props
  const who = customerName ?? "this customer"

  const { data: statusesData, error: statusesError } = useLicenseStatusesQuery()
  errorToast(statusesError, { title: "Failed to query for license statuses" })
  const statusNamesByCode = useMemo(
    () =>
      new Map(
        (statusesData?.getLicenseStatuses ?? []).map((status) => [
          status.Code,
          status.Name,
        ]),
      ),
    [statusesData?.getLicenseStatuses],
  )

  // The package catalog, for the Unlocks pills' links; the same small read
  // the What they get tab and the package boards share.
  const { data: packagesData } = useFeatureGroupsQuery()
  const packageIdByName = useMemo(
    () =>
      new Map(
        (packagesData?.getFeatureGroups ?? []).map((featureGroup) => [
          featureGroup.Name,
          featureGroup.Id,
        ]),
      ),
    [packagesData?.getFeatureGroups],
  )

  const columns = useMemo(
    () =>
      createAddOnColumns({
        who,
        statusNameOf: (code) => statusNamesByCode.get(code),
        packageIdByName,
      }),
    [who, statusNamesByCode, packageIdByName],
  )

  return (
    <Table
      className={cn("license-add-on-table", className)}
      tableId="customer-add-on-table"
      data={addOns}
      columns={columns}
      getRowId={(row) => String(row.AddOnId)}
      {...tableProps}
    >
      <Table.Toolbar>
        <div className="flex-1" />
        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu onRefresh={onRefresh} exportFileName="add-ons">
          <LicenseAddOnManageButton
            customerId={customerId}
            customerName={customerName}
          />
        </Table.ToolbarMenu>
      </Table.Toolbar>

      <Table.FilterList />

      <Table.RowToolbar<CustomerAddOn> column="AddOnName">
        {(addOn) => (
          <LicenseAddOnTableRowToolbar
            addOn={addOn}
            customerId={customerId}
            who={who}
          />
        )}
      </Table.RowToolbar>

      <Table.Content
        isLoading={isLoading}
        error={error}
        // Pinned to the visible left edge: the table is wider than the panel,
        // so a centred line would sit half out of view.
        renderEmpty={
          <p className="sticky left-0 max-w-sm p-4 text-sm text-muted-foreground">
            No add-ons yet. {who}&apos;s users have what their licenses include;
            an add-on gives them more on top of a license.
          </p>
        }
      />
    </Table>
  )
}

export default LicenseAddOnTable
